import React, { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import cameras from "../data/cameras";
import scenarios from "../data/scenarios.json";
import lightingConditions from "../data/lighting.json";
import movements from "../data/movement.json";
import looks from "../data/looks.json";
import outputs from "../data/output.json";
import curatedCombos from "../data/recipes-curated.json";

import { generateRecipe } from "../engine/ruleEngine";
import { generateExplanation } from "../engine/explanationEngine";
import AccessGate from "../components/AccessGate";
import MobileSettingsCard from "../components/MobileSettingsCard";
import SceneAnalyzer from "../components/SceneAnalyzer";
import { setPageMeta } from "../utils/meta";

/**
 * The on-set page. Scanned from a QR code, opened on a phone, usually with
 * a camera in the other hand.
 *
 * The settings are NOT gated any more, and that change matters. The same
 * recipe is already fully public at /camera/:slug, so a lock here was
 * protecting nothing — while stopping the one person who had most earned
 * the answer, at the exact moment they needed it. Someone who scanned a
 * code you handed them should see the numbers immediately.
 *
 * What's gated instead is the thing that isn't published anywhere else:
 * live measurement from the phone's own sensors.
 */
export default function ViewGate() {
  const { slug } = useParams();
  const combo = curatedCombos.find((c) => c.slug === slug);

  useEffect(() => {
    // Not indexed: this page is reached by scanning a code someone handed
    // you, and a searcher landing here instead of the public recipe page
    // would get the worse of the two versions. Follow (not nofollow),
    // because the analyzer links out to the companion app and there's no
    // reason to block that path from being crawled elsewhere.
    setPageMeta({
      title: "Camera settings",
      description: "Scanned settings card.",
      noindex: true,
      follow: false,
    });
  }, []);

  const data = useMemo(() => {
    if (!combo) return null;
    const camera = cameras.find((c) => c.id === combo.cameraId);
    const scenario = scenarios.find((s) => s.id === combo.scenarioId);
    const lighting = lightingConditions.find((l) => l.id === combo.lightingId);
    if (!camera || !scenario || !lighting) return null;
    const movement = movements.find((m) => m.id === scenario.movementId);
    const look = looks.find((l) => l.id === combo.lookId) || looks[0];
    const output = outputs.find((o) => o.id === (combo.outputId || "youtube-long"));
    const recipe = generateRecipe({ camera, scenario, lighting, movement, look, output });
    const explanation = generateExplanation(recipe, { camera, scenario, lighting }, undefined, combo.publishAt);
    return { camera, scenario, lighting, recipe, explanation };
  }, [combo]);

  if (!combo || !data) {
    return (
      <div className="viewgate-page">
        <div className="gate-card">
          <h2>Link not found</h2>
          <p>This settings link doesn't match anything we have.</p>
        </div>
      </div>
    );
  }

  // The analyzer compares a live reading against what this page assumed,
  // so it needs the assumption itself, not just the resulting numbers.
  const analyzerRecipe = {
    ...data.recipe,
    lightingName: data.lighting.name,
    lightingExposureStops: data.lighting.exposureStops,
  };

  return (
    <div className="viewgate-page">
      <MobileSettingsCard
        camera={data.camera}
        scenario={data.scenario}
        lighting={data.lighting}
        recipe={data.recipe}
        explanation={data.explanation}
      />

      <section className="viewgate-analyze" aria-labelledby="analyze-heading">
        <h2 id="analyze-heading">Check it against the light you're actually in</h2>
        <p className="viewgate-analyze-dek">
          These settings assume {data.lighting.name.toLowerCase()}. Real conditions drift from that, 
          thin cloud, a bright wall bouncing fill, an hour later than you planned.
        </p>

        <AccessGate
          storageKey={`analyze:${slug}`}
          title="Unlock on-device analysis"
          teaser="Measure your actual pan speed and light with the phone's own sensors, then compare against what this recipe assumed."
        >
          <SceneAnalyzer recipe={analyzerRecipe} camera={data.camera} />
        </AccessGate>
      </section>
    </div>
  );
}
